/**
 * MKS-136 — the project's billing schedule and the lead's drafted phases.
 *
 * Every mutation invalidates the whole schedule rather than patching one row:
 * a period's billed range is defined by the period before it, and its amount
 * moves the Fixed Price sum check — both of which are computed server side over
 * the WHOLE list, so a single-row update would leave the rest of the table
 * disagreeing with itself.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  cancelBillingPeriod,
  createBillingPeriod,
  fetchBillingSchedule,
  fetchLeadBillingPhases,
  markBillingPeriodDone,
  putLeadBillingPhases,
  regenerateBillingPeriods,
  setTaskBillingPeriod,
  updateBillingPeriod,
} from '../services/api/billingPeriods';
import type {
  CreateBillingPeriodInput,
  LeadBillingPhaseInput,
  UpdateBillingPeriodInput,
} from '../services/api/billingPeriods';

export const billingScheduleKey = (projectId: number | undefined) =>
  ['billingSchedule', projectId] as const;

export function useBillingSchedule(projectId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: billingScheduleKey(projectId),
    queryFn: () => fetchBillingSchedule(projectId!),
    enabled: !!projectId && enabled,
  });
}

export function useCreateBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, input }: { projectId: number; input: CreateBillingPeriodInput }) =>
      createBillingPeriod(projectId, input),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: billingScheduleKey(projectId) });
    },
  });
}

export function useUpdateBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      periodId,
      input,
    }: {
      projectId: number;
      periodId: number;
      input: UpdateBillingPeriodInput;
    }) => updateBillingPeriod(periodId, input),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: billingScheduleKey(projectId) });
    },
  });
}

export function useMarkBillingPeriodDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      periodId,
      actualEndDate,
    }: {
      projectId: number;
      periodId: number;
      actualEndDate?: string;
    }) => markBillingPeriodDone(periodId, actualEndDate),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: billingScheduleKey(projectId) });
    },
  });
}

export function useCancelBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId }: { projectId: number; periodId: number }) =>
      cancelBillingPeriod(periodId),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: billingScheduleKey(projectId) });
    },
  });
}

export function useRegenerateBillingPeriods() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId }: { projectId: number }) => regenerateBillingPeriods(projectId),
    onSuccess: (_d, { projectId }) => {
      qc.invalidateQueries({ queryKey: billingScheduleKey(projectId) });
    },
  });
}

export function useSetTaskBillingPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      billingPeriodId,
    }: {
      taskId: number;
      billingPeriodId: number | null;
    }) => setTaskBillingPeriod(taskId, billingPeriodId),
    onSuccess: () => {
      // The board caches the card, which now carries `billingPeriodId`.
      qc.invalidateQueries({ queryKey: ['board'] });
      qc.invalidateQueries({ queryKey: ['taskDetail'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Lead draft phases
// ---------------------------------------------------------------------------

export const leadBillingPhasesKey = (leadId: number | undefined) =>
  ['leadBillingPhases', leadId] as const;

export function useLeadBillingPhases(leadId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: leadBillingPhasesKey(leadId),
    queryFn: () => fetchLeadBillingPhases(leadId!),
    enabled: !!leadId && enabled,
  });
}

export function usePutLeadBillingPhases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, phases }: { leadId: number; phases: LeadBillingPhaseInput[] }) =>
      putLeadBillingPhases(leadId, phases),
    onSuccess: (_d, { leadId }) => {
      qc.invalidateQueries({ queryKey: leadBillingPhasesKey(leadId) });
    },
  });
}
